import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Tab,
  Tabs,
} from '@mui/material';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useTopPanel from 'hooks/useTopPanel';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const EXPORT_TYPES = [
  { key: 'customs', label: 'excel.tabs.customs' },
  { key: 'contracts', label: 'excel.tabs.contracts' },
  { key: 'debitors', label: 'excel.tabs.debitors' },
  { key: 'income', label: 'excel.tabs.income' },
];

export default function Excel() {
  const axiosPrivate = useAxiosPrivate();
  const { setComponent } = useTopPanel();
  const { t, i18n } = useTranslation();

  const [activeTab, setActiveTab] = useState(0);
  const [columns, setColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [columnsLoading, setColumnsLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const currentType = EXPORT_TYPES[activeTab].key;

  useEffect(() => {
    setComponent(
      <div className='component-title'>{t('excel.title')}</div>,
    );
  }, [i18n.language]);

  useEffect(() => {
    fetchColumns(currentType);
  }, [currentType]);

  const fetchColumns = async (type) => {
    setColumnsLoading(true);
    setColumns([]);
    setSelectedColumns([]);
    try {
      const res = await axiosPrivate.get(`/admin/excel/columns/${type}`);
      const data = res?.data?.data;
      const colsArray = Array.isArray(data) ? data : (data?.columns ?? []);
      const parsed = colsArray.map((item) => ({
        code: item.key ?? item.code ?? String(item),
        label: item.label ?? item.name ?? item.key ?? String(item),
      }));
      setColumns(parsed);
      setSelectedColumns(parsed.map((c) => c.code));
    } catch {
      setColumns([]);
      setSelectedColumns([]);
    } finally {
      setColumnsLoading(false);
    }
  };

  const toggleColumn = (code) => {
    setSelectedColumns((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const toggleAll = () => {
    if (selectedColumns.length === columns.length) {
      setSelectedColumns([]);
    } else {
      setSelectedColumns(columns.map((c) => c.code));
    }
  };

  const handleExport = async () => {
    if (selectedColumns.length === 0) return;
    setExportLoading(true);
    try {
      const params = new URLSearchParams();
      selectedColumns.forEach((col) => params.append('columns[]', col));
      const res = await axiosPrivate.get(
        `/admin/excel/${currentType}?${params.toString()}`,
      );
      const link = res?.data?.data?.link;
      const filename = res?.data?.data?.filename || 'export.xlsx';
      if (link) {
        const blob = await axiosPrivate.get(link, { responseType: 'blob' });
        const url = URL.createObjectURL(blob.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } finally {
      setExportLoading(false);
    }
  };

  const allSelected = columns.length > 0 && selectedColumns.length === columns.length;
  const someSelected = selectedColumns.length > 0 && selectedColumns.length < columns.length;

  return (
    <div className='component-list-wrapper'>
      <div className='component-list-header mb-4'>
        <div className='header-actions-container pt-2 pb-2 flex items-center justify-between'>
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            variant='scrollable'
            scrollButtons='auto'
          >
            {EXPORT_TYPES.map((type) => (
              <Tab key={type.key} label={t(type.label)} />
            ))}
          </Tabs>
          <Button
            variant='contained'
            color='success'
            disabled={selectedColumns.length === 0 || exportLoading}
            onClick={handleExport}
            startIcon={
              exportLoading ? (
                <CircularProgress size={16} color='inherit' />
              ) : (
                <i className='bi bi-download' />
              )
            }
          >
            {t('excel.export')}
          </Button>
        </div>
      </div>

      <div className='bg-white rounded-lg p-6'>
        {columnsLoading ? (
          <div className='flex items-center justify-center h-40'>
            <CircularProgress size={30} color='inherit' />
          </div>
        ) : columns.length === 0 ? (
          <div className='flex items-center justify-center h-40 text-gray-400'>
            <i className='bi bi-exclamation-octagon text-xl mr-2' />
            {t('common.global.noDataFound')}
          </div>
        ) : (
          <>
            <div className='mb-4 border-b pb-3'>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                  />
                }
                label={
                  <span className='font-semibold text-gray-700'>
                    {t('excel.selectAll')} ({selectedColumns.length}/{columns.length})
                  </span>
                }
              />
            </div>
            <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2'>
              {columns.map((col) => (
                <FormControlLabel
                  key={col.code}
                  control={
                    <Checkbox
                      checked={selectedColumns.includes(col.code)}
                      onChange={() => toggleColumn(col.code)}
                      size='small'
                    />
                  }
                  label={
                    <span className='text-sm text-gray-700'>{col.label}</span>
                  }
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
